import type { BoundaryKind, CoreProjection, CosmosDataModel } from '@/adapters/core/_contract/config';
import { TYPE_CORE_TREE } from '@/adapters/core/_data/config/fragments/resourceTypes';

type CoreProjectionNode = CoreProjection['children'][string];

function isCoreProjectionNode(value: unknown, expectedPath = '.'): value is CoreProjectionNode {
    if (typeof value !== 'object' || value === null) return false;
    if (!('path' in value) || value.path !== expectedPath) return false;
    if (!('nodeKind' in value)) return false;
    if (value.nodeKind === 'file') return !('children' in value) && !('name' in value);
    return value.nodeKind === 'directory'
        && 'children' in value
        && typeof value.children === 'object'
        && value.children !== null
        && !Array.isArray(value.children)
        && !('name' in value)
        && Object.entries(value.children).every(([name, child]) =>
            name !== '.' && name !== '..' && !/[\\/\u0000]/.test(name) && name.length > 0
            && isCoreProjectionNode(child, expectedPath === '.' ? name : `${expectedPath}/${name}`));
}

export function getCoreProjection(cosmosData: CosmosDataModel): CoreProjection {
    const resources = cosmosData.resourcesByType[TYPE_CORE_TREE];
    if (resources?.length !== 1) {
        throw new Error(`Core space requires exactly one ${TYPE_CORE_TREE} resource.`);
    }

    const resource = resources[0];
    const projection = resource.projection;
    if (resource.resourceTypeHandle !== TYPE_CORE_TREE
        || !isCoreProjectionNode(projection)
        || projection.nodeKind !== 'directory') {
        throw new Error(`Invalid ${TYPE_CORE_TREE} resource: expected a directory tree projection.`);
    }

    return projection;
}

export type VisualNode = {
    nodeKind: 'directory' | 'file';
    name: string;
    path: string;
    parentPath: string | null;
    depth: number;
    childPaths: string[];
    boundaryKind: BoundaryKind | null;
};

const BOUNDARY_KINDS: readonly string[] = ['_contract', '_data', '_pure'];

export function toInnermostBoundaryKind(path: string): BoundaryKind | null {
    const segments = path.split('/');
    for (let i = segments.length - 1; i >= 0; i--) {
        if (BOUNDARY_KINDS.includes(segments[i]!)) return segments[i] as BoundaryKind;
    }
    return null;
}
